import { useState, useEffect } from 'react';
import { useAppNavigate } from '../contexts/PwaNavContext';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import axios from 'axios';
import { API_URL } from '../utils/api';
import CustomerUserMenu from '../components/CustomerUserMenu';
import './OrderHistory.css';

interface OrderItem {
  id: string;
  quantity: number;
  price: number;
  product: {
    name: string;
    imageUrl: string | null;
  };
}

interface Order {
  id: string;
  status: 'PENDING' | 'PREPARING' | 'READY' | 'COMPLETED' | 'CANCELLED';
  totalAmount: number;
  items: OrderItem[];
  restaurant: {
    name: string;
  };
  createdAt: string;
  qrCode: string;
}

function OrderHistory() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customerId, setCustomerId] = useState('');
  const navigate = useAppNavigate();

  useEffect(() => {
    // For MVP, we'll use a simple approach - get orders by customer ID from URL or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('customerId') || localStorage.getItem('olso_customer_id') || '';
    setCustomerId(id);
    
    if (id) {
      fetchOrders(id);
    } else {
      setError('orderHistory.noCustomerId');
      setLoading(false);
    }
  }, []);

  const fetchOrders = async (id: string) => {
    setLoading(true);
    setError('');
    
    try {
      // For now, we'll need to add this endpoint to backend
      // For MVP, we can show a message that this feature is coming
      const response = await axios.get(`${API_URL}/api/customers/${id}/orders`);
      setOrders(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('orderHistory.customerNotFound');
      } else {
        setError('orderHistory.failedLoad');
      }
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (qrCode: string) => {
    navigate(`/order/${qrCode}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'var(--a-green-mint)';
      case 'READY':
        return 'var(--a-green-mint)';
      case 'PREPARING':
        return 'var(--a-amber-hot)';
      case 'PENDING':
        return 'var(--a-amber-warm-2)';
      case 'CANCELLED':
        return 'var(--a-red-salmon-3)';
      default:
        return '#999';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return t('orders.completed');
      case 'READY':
        return t('orders.ready');
      case 'PREPARING':
        return t('orders.preparing');
      case 'PENDING':
        return t('orders.inQueue');
      case 'CANCELLED':
        return t('orderStatus.cancelled');
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(getDateLocale(), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="order-history-container">
        <button className="oh-back-btn oh-back-btn--abs" onClick={() => navigate('/')} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <div className="card">
          <div className="loading-state">{t('orderHistory.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-history-container">
      <div className="card">
        <div className="top-row">
          <button className="oh-back-btn" onClick={() => navigate('/')} aria-label="Back">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
          <div className="brand-title">{t('orderHistory.title')}</div>
          <CustomerUserMenu />
        </div>

        {error ? (
          <div className="error-message">
            {error.startsWith('orderHistory.') ? t(error) : error}
            {!customerId && (
              <div className="error-help">
                <p>{t('orderHistory.errorHelp1')}</p>
                <p>{t('orderHistory.errorHelp2')}</p>
              </div>
            )}
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"></div>
            <h3>{t('orderHistory.noOrdersYet')}</h3>
            <p>{t('orderHistory.noOrdersDesc')}</p>
          </div>
        ) : (
          <>
            <div className="orders-header">
              <h2>{t('orderHistory.yourOrders')}</h2>
              <span className="orders-count">{orders.length} {orders.length === 1 ? t('orderHistory.order') : t('orderHistory.orders')}</span>
            </div>

            <div className="orders-list">
              {orders.map((order) => (
                <div key={order.id} className="order-history-card" onClick={() => handleViewOrder(order.qrCode)}>
                  <div className="order-card-header">
                    <div className="order-info">
                      <div className="order-number">#{order.id.slice(-6).toUpperCase()}</div>
                      <div className="order-restaurant">{order.restaurant.name}</div>
                    </div>
                    <div
                      className="order-status-badge"
                      style={{ backgroundColor: getStatusColor(order.status) + '20', color: getStatusColor(order.status) }}
                    >
                      {getStatusLabel(order.status)}
                    </div>
                  </div>

                  <div className="order-items-preview">
                    {order.items.slice(0, 3).map((item) => (
                      <div key={item.id} className="preview-item">
                        {item.product.imageUrl ? (
                          <div
                            className="preview-image"
                            style={{ backgroundImage: `url(${item.product.imageUrl})` }}
                          />
                        ) : (
                          <div className="preview-image-placeholder"></div>
                        )}
                        <span className="preview-name">
                          {item.product.name} {item.quantity > 1 && `×${item.quantity}`}
                        </span>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <div className="preview-more">+{order.items.length - 3} {t('orderHistory.moreItems')}</div>
                    )}
                  </div>

                  <div className="order-card-footer">
                    <div className="order-date">{formatDate(order.createdAt)}</div>
                    <div className="order-total">₸{order.totalAmount.toFixed(2)}</div>
                  </div>

                  <button className="view-order-btn">{t('orderHistory.viewDetails')} →</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default OrderHistory;

